import { supabase, requireOrganizationId } from "./supabase";
import { roundCurrency } from "./claimFinancials";
import {
  buildReconciliationSummaryFromLineItems,
  getOperationalRetentionPct,
  type ReconciliationSummary,
} from "./reconciliationSummary";
import type { Json } from "../types/database.types";
import { logError, logWarn } from "./logError";
import { unpayHireRowsOfReport } from "./hireUnpay";

// ─── Types ────────────────────────────────────────────────────────
export type PaymentAction =
  | "pending"
  | "paid"
  | "voided"
  | "held"
  | "agreement_made"
  | "works_not_completed";

export type PaymentLineType = "progress" | "variation" | "retention" | "hire";

export interface PaymentLineItem {
  id?: string;
  organization_id?: string;
  report_id: string;
  line_type: PaymentLineType;
  subcategory_id?: string | null;
  variation_id?: string | null;
  hire_billing_id?: string | null;
  claimed_percentage: number;
  claimed_amount: number;
  paid_percentage: number;
  paid_amount: number;
  action: PaymentAction;
  agreed_amount?: number | null;
  notes?: string | null;
  pushed_to_month?: number | null;
  pushed_to_year?: number | null;
  created_at?: string;
  updated_at?: string;
}

/** Lookup map key for Range View cross-reference */
export interface PaymentCellKey {
  subcategoryId: string;
  month: number;
  year: number;
}

export interface PaymentCellInfo {
  action: PaymentAction;
  claimed_percentage: number;
  paid_percentage: number;
  claimed_amount: number;
  paid_amount: number;
  agreed_amount?: number | null;
  notes?: string | null;
}

export interface SavePaymentReconciliationInput {
  reportId: string;
  lineItems: PaymentLineItem[];
  progressPaymentPct: number;
  variationPayments: Json | null;
  paidByBuilder: number;
  calculatedPaymentAmount: number;
  difference: number;
  datePaid: string | null;
  daysBeforeDue: number | null;
  hasUnresolvedItems: boolean;
  retentionPct: number;
  retentionAmount: number;
  retentionReceivedAmount: number | null;
  retentionReceivedNotes: string | null;
  retentionReceivedAt: string | null;
  reconciliationSummary: ReconciliationSummary | null;
  changesSummary?: string;
  changedByName?: string | null;
}

export interface PaymentReconciliationReport {
  id: string;
  total_exc_gst?: number | null;
  paid_by_builder?: number | null;
  retention_pct?: number | null;
  retention_amount?: number | null;
  progress_payment_pct?: number | null;
  date_paid?: string | null;
  days_before_due?: number | null;
  retention_received_amount?: number | null;
  retention_received_notes?: string | null;
  retention_received_at?: string | null;
  variation_payments?: Json | null;
  retention_release_amount?: number | null;
  reconciliation_summary?: unknown;
}

export interface BuildPaymentReconciliationInputParams {
  report: PaymentReconciliationReport;
  lineItems: PaymentLineItem[];
  datePaid: string | null;
  daysBeforeDue: number | null;
  changesSummary?: string;
  changedByName?: string | null;
  variationPayments?: Json | null;
  retentionReceivedAmount?: number | null;
  retentionReceivedNotes?: string | null;
  retentionReceivedAt?: string | null;
}

interface SupabaseRpcError {
  message: string;
}

// ─── Fetch Helpers ────────────────────────────────────────────────

/**
 * claims_payment_line_items has two foreign keys to claims_reports
 * (report_id, and the composite report_id + organization_id). An embed that
 * names only the table is ambiguous, PostgREST answers HTTP 300 (PGRST201) and
 * the caller gets no rows. Every embed of claims_reports must name this key.
 */
export const LINE_ITEMS_TO_REPORTS_FK =
  "claims_payment_line_items_report_id_fkey";

export const PROJECT_LINE_ITEMS_SELECT = `
      *,
      claims_reports!${LINE_ITEMS_TO_REPORTS_FK}!inner (
        claim_month,
        claim_year,
        project_id
      )
    `;

export const VARIATION_HISTORY_SELECT = `
      variation_id,
      report_id,
      paid_amount,
      action,
      claims_reports!${LINE_ITEMS_TO_REPORTS_FK}!inner (
        claim_number,
        claim_month,
        claim_year
      )
    `;

/**
 * Fetch all payment line items for a specific report
 */
export async function fetchPaymentLineItems(
  reportId: string
): Promise<PaymentLineItem[]> {
  const { data, error } = await supabase
    .from("claims_payment_line_items")
    .select("*")
    .eq("report_id", reportId)
    .order("created_at", { ascending: true });

  if (error) {
    logError("[PaymentLineItems] Fetch error:", error);
    return [];
  }

  return (data as unknown as PaymentLineItem[]) || [];
}

/**
 * Fetch all payment line items for a project (used by Range View).
 * Joins through claims_reports to get month/year context.
 */
export async function fetchPaymentLineItemsByProject(
  projectId: string
): Promise<
  (PaymentLineItem & { claim_month: number; claim_year: number })[]
> {
  const { data, error } = await supabase
    .from("claims_payment_line_items")
    .select(PROJECT_LINE_ITEMS_SELECT)
    .eq("claims_reports.project_id", projectId);

  if (error) {
    logError("[PaymentLineItems] Project fetch error", error);
    // SEQ-247: throw so the Range view can tell the user. Returning [] here
    // looked exactly like "no payments" and hid the failure.
    throw error;
  }

  type JoinedPaymentLineItem = PaymentLineItem & {
    claims_reports?: {
      claim_month?: number | null;
      claim_year?: number | null;
    } | null;
  };

  return ((data as unknown as JoinedPaymentLineItem[]) || []).map((row) => ({
    ...row,
    claim_month: Number(row.claims_reports?.claim_month || 0),
    claim_year: Number(row.claims_reports?.claim_year || 0),
    claims_reports: undefined,
  }));
}

/**
 * Fetch payment history for specific variations across ALL reports.
 * Returns which claim paid each variation and how much.
 */
export interface VariationPaymentHistoryItem {
  variation_id: string;
  report_id: string;
  claim_number: number;
  claim_month: number;
  claim_year: number;
  paid_amount: number;
  action: PaymentAction;
}

export async function fetchVariationPaymentHistory(
  variationIds: string[]
): Promise<VariationPaymentHistoryItem[]> {
  if (variationIds.length === 0) return [];

  const { data, error } = await supabase
    .from("claims_payment_line_items")
    .select(VARIATION_HISTORY_SELECT)
    .eq("line_type", "variation")
    .in("variation_id", variationIds);

  if (error) {
    logError("[PaymentLineItems] Variation history error, payment history is empty", error);
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data as any[]) || []).map((row) => ({
    variation_id: row.variation_id,
    report_id: row.report_id,
    claim_number: Number(row.claims_reports?.claim_number || 0),
    claim_month: Number(row.claims_reports?.claim_month || 0),
    claim_year: Number(row.claims_reports?.claim_year || 0),
    paid_amount: Number(row.paid_amount || 0),
    action: row.action as PaymentAction,
  }));
}
/**
 * Build a lookup map for Range View split-cell rendering.
 * Key format: "subcategoryId|month|year"
 */
export function buildPaymentCellMap(
  lineItems: (PaymentLineItem & { claim_month: number; claim_year: number })[]
): Map<string, PaymentCellInfo> {
  const map = new Map<string, PaymentCellInfo>();

  for (const item of lineItems) {
    if (item.line_type !== "progress" || !item.subcategory_id) continue;

    const key = `${item.subcategory_id}|${item.claim_month}|${item.claim_year}`;
    map.set(key, {
      action: item.action,
      claimed_percentage: item.claimed_percentage,
      paid_percentage: item.paid_percentage,
      claimed_amount: item.claimed_amount,
      paid_amount: item.paid_amount,
      agreed_amount: item.agreed_amount,
      notes: item.notes,
    });
  }

  return map;
}

// ─── Save Helpers ─────────────────────────────────────────────────

function paymentActionCountsAsReceived(item: PaymentLineItem): boolean {
  return item.action === "paid" || item.action === "agreement_made";
}

async function getCurrentPaymentActorName(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) return null;

  const { data: profile } = await supabase
    .from("profiles")
    .select("full_name")
    .eq("id", user.id)
    .single();

  const fullName = (profile as { full_name?: string | null } | null)?.full_name;
  return fullName || user.email || null;
}

export function mergePaymentLineItemsByType(
  existingItems: PaymentLineItem[],
  lineType: PaymentLineType,
  replacementItems: PaymentLineItem[]
): PaymentLineItem[] {
  return [
    ...existingItems.filter((item) => item.line_type !== lineType),
    ...replacementItems,
  ];
}

export function buildPaymentReconciliationInput({
  report,
  lineItems,
  datePaid,
  daysBeforeDue,
  changesSummary,
  changedByName,
  variationPayments,
  retentionReceivedAmount,
  retentionReceivedNotes,
  retentionReceivedAt,
}: BuildPaymentReconciliationInputParams): SavePaymentReconciliationInput {
  const progressItems = lineItems.filter((item) => item.line_type === "progress");
  const variationItems = lineItems.filter((item) => item.line_type === "variation");
  const hireItems = lineItems.filter((item) => item.line_type === "hire");
  const retentionItems = lineItems.filter((item) => item.line_type === "retention");

  const progressClaimed = progressItems.reduce(
    (sum, item) => sum + Number(item.claimed_amount || 0),
    0
  );
  const progressPaid = progressItems.reduce(
    (sum, item) =>
      sum + (paymentActionCountsAsReceived(item) ? Number(item.paid_amount || 0) : 0),
    0
  );
  const variationPaid = variationItems.reduce(
    (sum, item) =>
      sum + (paymentActionCountsAsReceived(item) ? Number(item.paid_amount || 0) : 0),
    0
  );
  const hirePaid = hireItems.reduce(
    (sum, item) =>
      sum + (paymentActionCountsAsReceived(item) ? Number(item.paid_amount || 0) : 0),
    0
  );
  const retentionReleased = retentionItems.reduce(
    (sum, item) => sum + (item.action === "paid" ? Number(item.paid_amount || 0) : 0),
    0
  );

  const retentionPct = getOperationalRetentionPct(report);
  const nonRetentionPaid = roundCurrency(progressPaid + variationPaid + hirePaid);
  const amountReceived = roundCurrency(nonRetentionPaid + retentionReleased);
  const reconciliationSummary = buildReconciliationSummaryFromLineItems({
    lineItems,
    retentionPct,
    retentionHeldEnabled: retentionPct > 0,
    amountReceivedIncGst: amountReceived,
  });
  const hasPaymentEvent = lineItems.length > 0;
  const nextVariationPayments =
    variationPayments !== undefined
      ? variationPayments
      : variationItems.length > 0
        ? (variationItems.map((item) => ({
          variation_id: item.variation_id,
          paid_pct: item.paid_percentage,
          paid_amount: item.paid_amount,
          action: item.action,
        })) as unknown as Json)
        : report.variation_payments ?? null;

  return {
    reportId: report.id,
    lineItems,
    progressPaymentPct:
      progressClaimed > 0
        ? roundCurrency((progressPaid / progressClaimed) * 100)
        : Number(report.progress_payment_pct || 0),
    variationPayments: nextVariationPayments,
    paidByBuilder: reconciliationSummary.amount_received_inc_gst,
    calculatedPaymentAmount: reconciliationSummary.calculated_payment_inc_gst,
    difference: reconciliationSummary.difference_inc_gst,
    datePaid: hasPaymentEvent ? datePaid || report.date_paid || null : null,
    daysBeforeDue: hasPaymentEvent ? daysBeforeDue ?? report.days_before_due ?? null : null,
    hasUnresolvedItems: lineItems.some((item) => item.action !== "paid"),
    retentionPct,
    retentionAmount: reconciliationSummary.retention_held_ex_gst,
    retentionReceivedAmount:
      retentionReceivedAmount !== undefined
        ? retentionReceivedAmount
        : report.retention_received_amount ?? null,
    retentionReceivedNotes:
      retentionReceivedNotes !== undefined
        ? retentionReceivedNotes
        : report.retention_received_notes ?? null,
    retentionReceivedAt:
      retentionReceivedAt !== undefined
        ? retentionReceivedAt
        : report.retention_received_at ?? null,
    reconciliationSummary,
    changesSummary,
    changedByName,
  };
}

/**
 * Upsert all payment line items for a report.
 * Deletes existing items for the report, then inserts new ones.
 * This ensures clean state without complex diff logic.
 */
export async function upsertPaymentLineItems(
  reportId: string,
  items: PaymentLineItem[]
): Promise<{ success: boolean; error?: string }> {
  const orgId = requireOrganizationId();

  try {
    const { error: deleteError } = await supabase
      .from("claims_payment_line_items")
      .delete()
      .eq("report_id", reportId);

    if (deleteError) throw deleteError;

    // 2. Insert new items (skip if empty)
    if (items.length === 0) return { success: true };

    const insertData = items.map((item) => ({
      organization_id: orgId,
      report_id: reportId,
      line_type: item.line_type,
      subcategory_id: item.subcategory_id || null,
      variation_id: item.variation_id || null,
      hire_billing_id: item.hire_billing_id || null,
      claimed_percentage: item.claimed_percentage,
      claimed_amount: item.claimed_amount,
      paid_percentage: item.paid_percentage,
      paid_amount: item.paid_amount,
      action: item.action,
      agreed_amount: item.agreed_amount || null,
      notes: item.notes || null,
      pushed_to_month: item.pushed_to_month || null,
      pushed_to_year: item.pushed_to_year || null,
    }));

    const { error: insertError } = await supabase
      .from("claims_payment_line_items")
      .insert(insertData);

    if (insertError) throw insertError;

    return { success: true };
  } catch (err) {
    logError("[PaymentLineItems] Upsert error:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error",
    };
  }
}

export async function savePaymentReconciliation({
  reportId,
  lineItems,
  progressPaymentPct,
  variationPayments,
  paidByBuilder,
  calculatedPaymentAmount,
  difference,
  datePaid,
  daysBeforeDue,
  hasUnresolvedItems,
  retentionPct,
  retentionAmount,
  retentionReceivedAmount,
  retentionReceivedNotes,
  retentionReceivedAt,
  reconciliationSummary,
  changesSummary,
  changedByName,
}: SavePaymentReconciliationInput): Promise<{ success: boolean; error?: string }> {
  const rpcItems = lineItems.map((item) => ({
    line_type: item.line_type,
    subcategory_id: item.subcategory_id ?? null,
    variation_id: item.variation_id ?? null,
    hire_billing_id: item.hire_billing_id ?? null,
    claimed_percentage: item.claimed_percentage,
    claimed_amount: item.claimed_amount,
    paid_percentage: item.paid_percentage,
    paid_amount: item.paid_amount,
    action: item.action,
    agreed_amount: item.agreed_amount ?? null,
    notes: item.notes ?? null,
    pushed_to_month: item.pushed_to_month ?? null,
    pushed_to_year: item.pushed_to_year ?? null,
  })) as unknown as Json;

  const rpcParams: Record<string, unknown> = {
    p_report_id: reportId,
    p_line_items: rpcItems,
    p_progress_payment_pct: progressPaymentPct,
    p_variation_payments: variationPayments,
    p_paid_by_builder: paidByBuilder,
    p_calculated_payment_amount: calculatedPaymentAmount,
    p_difference: difference,
    p_date_paid: datePaid,
    p_days_before_due: daysBeforeDue,
    p_has_unresolved_items: hasUnresolvedItems,
    p_retention_pct: retentionPct,
    p_retention_amount: retentionAmount,
    p_retention_received_amount: retentionReceivedAmount,
    p_retention_received_notes: retentionReceivedNotes,
    p_retention_received_at: retentionReceivedAt,
    p_reconciliation_summary: reconciliationSummary as unknown as Json,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rpc = (supabase.rpc as any).bind(supabase) as (
    fn: string,
    args: Record<string, unknown>
  ) => Promise<{ error: SupabaseRpcError | null }>;
  const { error } =
    typeof changesSummary === "string"
      ? await rpc("replace_claim_payment_reconciliation_with_version_v2", {
        ...rpcParams,
        p_changes_summary: changesSummary,
        p_changed_by_name: changedByName ?? await getCurrentPaymentActorName(),
      })
      : await rpc("replace_claim_payment_reconciliation_v2", rpcParams);

  if (error) {
    logError("[PaymentLineItems] Reconciliation RPC error:", error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Clear ALL payment data for a report: deletes all line items and resets
 * the report's payment fields (paid_by_builder, date_paid, etc.).
 * Also resets variation approved_amounts and syncs $0 to Dashpivot.
 * Used by the "Clear Payment" action in the popover.
 */
export async function clearAllPaymentData(
  reportId: string,
  totalIncGst: number,
  reportMeta?: {
    projectId: string;
    claimMonth: number;
    claimYear: number;
  },
  changesSummary?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: reportRow, error: reportFetchError } = await supabase
      .from("claims_reports")
      .select("retention_pct, retention_amount")
      .eq("id", reportId)
      .single();

    if (reportFetchError) throw reportFetchError;

    const cleared = await savePaymentReconciliation({
      reportId,
      lineItems: [],
      progressPaymentPct: 0,
      variationPayments: null,
      paidByBuilder: 0,
      calculatedPaymentAmount: 0,
      difference: totalIncGst,
      datePaid: null,
      daysBeforeDue: null,
      hasUnresolvedItems: false,
      retentionPct: Number(reportRow?.retention_pct || 0),
      retentionAmount: Number(reportRow?.retention_amount || 0),
      retentionReceivedAmount: null,
      retentionReceivedNotes: null,
      retentionReceivedAt: null,
      reconciliationSummary: null,
      changesSummary,
    });

    if (!cleared.success) throw new Error(cleared.error || "Could not clear payment data");

    // Reset variation approved_amounts and push $0 to Dashpivot.
    // Full Reconciliation can include variations from prior months
    // (outstanding) and from the claimed_set, so the narrow
    // claim_month/claim_year filter would miss them. Instead:
    // 1. If the report has a claimed_set, reset those specific variation IDs.
    // 2. Otherwise, reset ALL project variations with approved_amount > 0.
    if (reportMeta) {
      // Fetch the report's claimed_set to know which variations were invoiced
      const { data: reportForClear } = await supabase
        .from("claims_reports")
        .select("claimed_set")
        .eq("id", reportId)
        .single();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const frozenVarIds: string[] = Array.isArray((reportForClear as any)?.claimed_set?.variations)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? (reportForClear as any).claimed_set.variations
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter((fv: any) => typeof fv.variation_id === "string" && !String(fv.variation_id).startsWith("__invoiced"))
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((fv: any) => fv.variation_id as string)
        : [];

      let variations: { id: string; total: number | null; discount_percent: number | null; dashpivot_form_id: string | null }[];

      if (frozenVarIds.length > 0) {
        // Reset only the variations referenced in the claimed_set
        // Deliberately NOT filtered by dashpivot_removed_at: addressed by the
        // report's frozen claimed_set ids, so reversing a recorded payment must
        // still reach a variation whose Dashpivot form later vanished.
        const { data } = await supabase
          .from("claims_variations")
          .select("id, total, discount_percent, dashpivot_form_id")
          .in("id", frozenVarIds);
        variations = (data as typeof variations) || [];
      } else {
        // Fallback: reset ALL project variations that have been partially/fully paid
        // Deliberately NOT filtered by dashpivot_removed_at: this legacy fallback
        // reverses payments already recorded against these rows, so excluding a
        // flagged one would strand real money nobody can unwind.
        const { data } = await supabase
          .from("claims_variations")
          .select("id, total, discount_percent, dashpivot_form_id")
          .eq("project_id", reportMeta.projectId)
          .gt("approved_amount", 0);
        variations = (data as typeof variations) || [];
      }

      if (variations.length > 0) {
        for (const v of variations) {
          const total = Number(v.total ?? 0);
          const discount = Number(v.discount_percent ?? 0);
          const effective = discount > 0 ? total * (1 - discount / 100) : total;

          await supabase
            .from("claims_variations")
            .update({
              approved_amount: 0,
              waiting_amount: Math.round(effective * 100) / 100,
            })
            .eq("id", v.id);

          // Push $0 to Dashpivot if linked
          if (v.dashpivot_form_id) {
            try {
              await supabase.functions.invoke("push-variation-update", {
                body: {
                  dashpivotFormId: v.dashpivot_form_id,
                  approvedAmount: 0,
                  projectId: reportMeta.projectId,
                },
              });
            } catch (pushErr) {
              logWarn(`[PaymentLineItems] Dashpivot push failed for variation ${v.id}:`, pushErr);
            }
          }
        }
      }

      // Install & Hire is paid from claims_hire_billing, not the parent
      // variation. Keep the billing row included in the claim, but clear the
      // receipt values when payment is cleared.
      type ClaimedHireBillingSnapshot = {
        hire_billing_id?: unknown;
        billing_id?: unknown;
      };
      type ClaimedSetSnapshot = {
        hire_billing?: ClaimedHireBillingSnapshot[];
      };
      const claimedSet = (reportForClear as { claimed_set?: ClaimedSetSnapshot | null } | null)
        ?.claimed_set;
      const frozenHireBillingIds: string[] = Array.isArray(claimedSet?.hire_billing)
        ? claimedSet.hire_billing
            .map((hb) => hb.hire_billing_id ?? hb.billing_id)
            .filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
        : [];

      let hireBillingIds = frozenHireBillingIds;
      if (hireBillingIds.length === 0) {
        const { data: rows } = await supabase
          .from("claims_hire_billing")
          .select("id")
          .eq("claim_report_id", reportId);
        hireBillingIds = ((rows as { id: string }[] | null) || []).map((row) => row.id);
      }

      // SEQ-218: only rows still attached to this report (or to none); a row
      // re-claimed onto a later claim is that claim's now.
      await unpayHireRowsOfReport(supabase, reportId, hireBillingIds);
    }

    return { success: true };
  } catch (err) {
    logError("[PaymentLineItems] Clear all payment data error:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error",
    };
  }
}

/**
 * Upsert payment line items for a SPECIFIC type only.
 * Deletes only items of the given line_type for the report, then inserts new ones.
 * Items of OTHER types are preserved, which is critical for standalone panel saves.
 */
export async function upsertPaymentLineItemsByType(
  reportId: string,
  lineType: PaymentLineType,
  items: PaymentLineItem[]
): Promise<{ success: boolean; error?: string }> {
  const orgId = requireOrganizationId();

  try {
    const { error: deleteError } = await supabase
      .from("claims_payment_line_items")
      .delete()
      .eq("report_id", reportId)
      .eq("line_type", lineType);

    if (deleteError) throw deleteError;

    // 2. Insert new items (skip if empty)
    if (items.length === 0) return { success: true };

    const insertData = items.map((item) => ({
      organization_id: orgId,
      report_id: reportId,
      line_type: lineType,
      subcategory_id: item.subcategory_id || null,
      variation_id: item.variation_id || null,
      hire_billing_id: item.hire_billing_id || null,
      claimed_percentage: item.claimed_percentage,
      claimed_amount: item.claimed_amount,
      paid_percentage: item.paid_percentage,
      paid_amount: item.paid_amount,
      action: item.action,
      agreed_amount: item.agreed_amount || null,
      notes: item.notes || null,
      pushed_to_month: item.pushed_to_month || null,
      pushed_to_year: item.pushed_to_year || null,
    }));

    const { error: insertError } = await supabase
      .from("claims_payment_line_items")
      .insert(insertData);

    if (insertError) throw insertError;

    return { success: true };
  } catch (err) {
    logError(`[PaymentLineItems] Upsert by type (${lineType}) error:`, err);
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error",
    };
  }
}

/**
 * After a standalone panel saves its own items, recalculate
 * the report's totals from ALL existing payment line items.
 * This ensures paid_by_builder stays correct when panels
 * save independently.
 */
export async function recalculateReportTotals(
  reportId: string,
  _totalIncGst: number
): Promise<void> {
  try {
    // Fetch all current line items for this report
    const allItems = await fetchPaymentLineItems(reportId);

    const isPaidForSchedule = (item: PaymentLineItem): boolean =>
      item.action === "paid" || item.action === "agreement_made";

    const progressAndVariationPaid = allItems
      .filter((li) => li.line_type !== "retention")
      .reduce(
        (sum, li) => sum + (isPaidForSchedule(li) ? Number(li.paid_amount || 0) : 0),
        0
      );

    // Retention released means money received back. Pending/held/agreed items do not count.
    const retentionReleased = allItems
      .filter((li) => li.line_type === "retention")
      .reduce(
        (sum, li) => sum + (li.action === "paid" ? Number(li.paid_amount || 0) : 0),
        0
      );

    const { data: reportRow, error: reportFetchError } = await supabase
      .from("claims_reports")
      .select("retention_pct, paid_by_builder, reconciliation_summary")
      .eq("id", reportId)
      .single();

    if (reportFetchError) throw reportFetchError;

    const retentionPct = getOperationalRetentionPct(reportRow || {});
    const existingReceived = roundCurrency(Number(reportRow?.paid_by_builder || 0));
    const lineItemReceived = roundCurrency(progressAndVariationPaid + retentionReleased);
    const amountReceived = existingReceived > 0 ? existingReceived : lineItemReceived;
    const reconciliationSummary = buildReconciliationSummaryFromLineItems({
      lineItems: allItems,
      retentionPct,
      retentionHeldEnabled: retentionPct > 0,
      amountReceivedIncGst: amountReceived,
    });

    const hasUnresolved = allItems.some((item) => item.action !== "paid");

    const reportUpdate: Record<string, unknown> = {
      paid_by_builder: reconciliationSummary.amount_received_inc_gst,
      calculated_payment_amount: reconciliationSummary.calculated_payment_inc_gst,
      difference: reconciliationSummary.difference_inc_gst,
      reconciliation_summary: reconciliationSummary as unknown as Json,
      has_unresolved_items: hasUnresolved,
    };

    if (reconciliationSummary.amount_received_inc_gst <= 0) {
      reportUpdate.date_paid = null;
      reportUpdate.days_before_due = null;
    }

    const { error: updateError } = await supabase
      .from("claims_reports")
      .update(reportUpdate)
      .eq("id", reportId);

    if (updateError) throw updateError;
  } catch (err) {
    logError("[PaymentLineItems] Recalculate totals error:", err);
    throw err;
  }
}

// ─── Action Helpers ───────────────────────────────────────────────

/**
 * "Works Not Completed": push a claims_progress entry to the next month.
 * Creates a new claims_progress row for the next month with the same percentage.
 */
export async function pushToNextMonth(
  subcategoryId: string,
  percentage: number,
  amount: number,
  currentMonth: number,
  currentYear: number
): Promise<{ month: number; year: number } | null> {
  const orgId = requireOrganizationId();

  // Calculate next month
  let nextMonth = currentMonth + 1;
  let nextYear = currentYear;
  if (nextMonth > 12) {
    nextMonth = 1;
    nextYear += 1;
  }

  try {
    // Check if entry already exists for next month
    const { data: existing } = await supabase
      .from("claims_progress")
      .select("id")
      .eq("subcategory_id", subcategoryId)
      .eq("claim_month", nextMonth)
      .eq("claim_year", nextYear)
      .limit(1)
      .maybeSingle();

    if (existing) {
      // Already has an entry, so don't duplicate, just return the target
      return { month: nextMonth, year: nextYear };
    }

    // Create new entry for next month
    const { error } = await supabase.from("claims_progress").insert({
      organization_id: orgId,
      subcategory_id: subcategoryId,
      claim_month: nextMonth,
      claim_year: nextYear,
      percentage,
      amount,
      input_type: "percentage",
    });

    if (error) {
      logError("[PaymentLineItems] Push to next month failed:", error);
      return null;
    }

    return { month: nextMonth, year: nextYear };
  } catch (err) {
    logError("[PaymentLineItems] Push to next month error:", err);
    return null;
  }
}

/**
 * Update the has_unresolved_items flag on a claims_report.
 * Call after saving line items to reflect if there are non-paid items.
 */
export async function updateUnresolvedFlag(
  reportId: string,
  lineItems: PaymentLineItem[]
): Promise<void> {
  const hasUnresolved = lineItems.some((item) => item.action !== "paid");

  await supabase
    .from("claims_reports")
    .update({ has_unresolved_items: hasUnresolved } as Record<string, unknown>)
    .eq("id", reportId);
}

// ─── Display Helpers ──────────────────────────────────────────────

/** Human-readable label for a payment action */
export function getActionLabel(action: PaymentAction): string {
  switch (action) {
    case "pending":
      return "Select Action";
    case "paid":
      return "Paid";
    case "voided":
      return "Voided";
    case "held":
      return "Held";
    case "agreement_made":
      return "Agreement Made";
    case "works_not_completed":
      return "Works Not Completed";
    default:
      return action;
  }
}

/** Color theme for a payment action */
export function getActionColor(action: PaymentAction): {
  bg: string;
  text: string;
  border: string;
  dotColor: string;
} {
  switch (action) {
    case "pending":
      return {
        bg: "#f8fafc",
        text: "#64748b",
        border: "#cbd5e1",
        dotColor: "#94a3b8",
      };
    case "paid":
      return {
        bg: "#f0fdf4",
        text: "#16a34a",
        border: "#bbf7d0",
        dotColor: "#16a34a",
      };
    case "voided":
      return {
        bg: "#fef2f2",
        text: "#dc2626",
        border: "#fecaca",
        dotColor: "#dc2626",
      };
    case "held":
      return {
        bg: "#fefce8",
        text: "#a16207",
        border: "#fde68a",
        dotColor: "#ca8a04",
      };
    case "agreement_made":
      return {
        bg: "#eff6ff",
        text: "#2563eb",
        border: "#bfdbfe",
        dotColor: "#2563eb",
      };
    case "works_not_completed":
      return {
        bg: "#fff7ed",
        text: "#ea580c",
        border: "#fed7aa",
        dotColor: "#ea580c",
      };
    default:
      return {
        bg: "#f8fafc",
        text: "#64748b",
        border: "#e2e8f0",
        dotColor: "#94a3b8",
      };
  }
}
